import { useEffect, useMemo, useRef, useState } from "react";
import {
    Sparkles, Search, Plus, MessageSquare, Send, ArrowDown, ChevronDown,
} from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { useEntity } from "@/lib/entityStore";
import { Loading } from "@/components/ds/PageShell";
import { AnswerLayout } from "@/components/ds/AnswerLayout";
import { EvidenceDrawer } from "@/components/ds/EvidenceDrawer";
import { SafeMarkdown } from "@/components/ds/SafeMarkdown";
import { DemoDataBanner } from "@/components/ds/DemoDataBanner";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// 8 commercially smart prompts — verbatim from owner direction.
const QUICK_PROMPTS = [
    { label: "What needs my attention today?", q: "What needs my attention today across the group?" },
    { label: "Which entity is dragging the group?", q: "Which entity is dragging the group, and by how much?" },
    { label: "Where is cash pressure building?", q: "Where is cash pressure building, and is it spreading?" },
    { label: "Who should we chase for money first?", q: "Who should we chase for money first — name the top 3 customers and amounts." },
    { label: "Are we safe to spend this month?", q: "Are we safe to spend at current pace this month? What's the buffer?" },
    { label: "What would I tell my accountant?", q: "What are the 3 things I should raise with my accountant this week?" },
    { label: "What changed this week?", q: "What changed materially this week across the group?" },
    { label: "Biggest risk nobody's watching?", q: "What's the biggest risk nobody is watching right now?" },
];

const EMPTY_PROMPTS = QUICK_PROMPTS;

// Premium AI loading cues — rotating context lines while the model thinks.
const THINKING_CUES = [
    "Reading the truth layer…",
    "Reviewing six entities…",
    "Reconciling cash position…",
    "Reading recent changes…",
    "Building the advisor view…",
];

const DAY_MS = 24 * 60 * 60 * 1000;
const RECENT_LIMIT = 12;

function bucketByDate(items) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today.getTime() - DAY_MS);
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());

    const buckets = { today: [], yesterday: [], week: [], older: [] };
    for (const c of items) {
        const dt = new Date(c.created_at);
        if (dt >= today) buckets.today.push(c);
        else if (dt >= yesterday) buckets.yesterday.push(c);
        else if (dt >= startOfWeek) buckets.week.push(c);
        else buckets.older.push(c);
    }
    return [
        { key: "today", label: "Today", items: buckets.today },
        { key: "yesterday", label: "Yesterday", items: buckets.yesterday },
        { key: "week", label: "Earlier this week", items: buckets.week },
        { key: "older", label: "Older", items: buckets.older },
    ].filter((g) => g.items.length > 0);
}

const STOPWORDS = new Set([
    "what", "the", "is", "are", "of", "in", "for", "and", "to", "a", "on",
    "be", "was", "with", "this", "that", "by", "from", "as", "an", "we",
    "i", "our", "my", "your", "their", "have", "has", "do", "does", "did",
]);

function autoTitle(question) {
    if (!question) return "New question";
    let t = question.trim().replace(/[?!.]+$/, "");
    // Capitalise first letter, drop trailing whitespace
    t = t.charAt(0).toUpperCase() + t.slice(1);
    if (t.length <= 48) return t;
    // Smart truncate at word boundary
    const cut = t.slice(0, 48);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > 24 ? cut.slice(0, lastSpace) : cut).trim() + "…";
}

function dedupeHistory(history) {
    // Keep the LAST occurrence of each unique question text (case-insensitive).
    const seen = new Map();
    for (const t of history) {
        const key = (t.question?.question || "").trim().toLowerCase();
        if (!key) continue;
        seen.set(key, t);
    }
    return Array.from(seen.values());
}

function NewConversationView({ onAsk, disabled }) {
    return (
        <div className="max-w-[680px] mx-auto pt-16 px-2 fade-up">
            <div className="flex items-center gap-2 text-stone-500 text-[12px] mb-4">
                <Sparkles size={13} strokeWidth={2} className="text-[var(--brand)]" />
                <span className="font-medium">Grounded in your truth layer</span>
            </div>
            <h1 className="text-display-md mb-3">Ask anything about the business.</h1>
            <p className="text-body text-stone-500 leading-relaxed max-w-prose">
                A calm CFO and operator advisor — grounded only in synced data and
                deterministic findings. It downgrades or refuses when data is missing,
                stale, or demo. Evidence stays one click away.
            </p>
            <div className="mt-10">
                <div className="text-[12px] uppercase tracking-wider text-stone-500 font-medium mb-3">
                    Try a question
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 stagger-children">
                    {EMPTY_PROMPTS.map((p, i) => (
                        <button
                            key={i}
                            type="button"
                            disabled={disabled}
                            onClick={() => onAsk(p.q)}
                            className="text-left rounded-lg border border-stone-200 bg-white px-4 py-3.5 hover:border-stone-300 hover:bg-stone-50 hover:shadow-[0_2px_6px_-4px_rgba(24,24,27,0.10)] transition disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid={`suggest-${i}`}
                        >
                            <div className="text-[14px] text-stone-800 leading-snug">
                                {p.label}
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ConversationItem({ turn, onOpenEvidence }) {
    const a = turn.answer;
    const q = turn.question;

    // Back-compat: if backend hasn't structured the response, treat answer_text as Bottom line
    const hasStructured =
        a?.direct?.trim() || a?.why?.trim() || (a?.actions || []).length > 0;
    const rawDirect = hasStructured
        ? (a.direct?.trim() || "")
        : (a?.answer_text?.trim() || "");
    // Strip the legacy "Based on MOCK/SANDBOX…" prefix from older answers — the
    // calm Demo data chip carries that context now.
    const direct = rawDirect.replace(
        /^based on (mock|sandbox|mock\/sandbox|demo)[^.\n]*[.\n]\s*/i,
        "",
    ).trim();
    const why = hasStructured ? (a.why?.trim() || "") : "";
    const actions = hasStructured ? (a.actions || []) : [];
    // Caveat: prefer the LLM's [CAVEAT] line; otherwise derive a calm fallback.
    let caveat = (a?.caveat || "").trim();
    if (!caveat) {
        const isDemo = a?.data_mode && a.data_mode !== "production_live";
        if (isDemo && a?.confidence) {
            caveat = `Based on demo data — live MYOB data is not connected yet.`;
        }
    }
    const evidenceCount =
        (a?.evidence_refs?.length || 0) + (a?.evidence_blocks?.length || 0);

    return (
        <div className="py-6 first:pt-4 fade-up">
            <div className="flex items-baseline gap-3 mb-3 text-[12px] text-stone-400">
                <span className="font-medium">
                    {q?.scope === "entity" ? "Entity" : "Group"}
                </span>
                <span>·</span>
                <span className="font-mono">{a?.created_at ? fmtDateTime(a.created_at) : ""}</span>
            </div>
            <div className="text-[18px] text-stone-800 leading-snug mb-5 font-medium">
                {q?.question}
            </div>
            <AnswerLayout
                direct={direct || "(no answer)"}
                why={why}
                actions={actions}
                caveat={caveat}
                confidence={a?.confidence}
                onOpenEvidence={() => onOpenEvidence(a)}
                evidenceHint={`${evidenceCount} refs`}
            />
        </div>
    );
}

export default function AIExpert() {
    const { selectedEntityId, selectedEntity, isGroup } = useEntity();
    const [question, setQuestion] = useState("");
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeQuestionId, setActiveQuestionId] = useState(null);
    const [search, setSearch] = useState("");
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [evidence, setEvidence] = useState(null);
    const [showScrollDown, setShowScrollDown] = useState(false);
    const scrollRef = useRef(null);
    const endRef = useRef(null);

    const loadHistory = async () => {
        try {
            const { data } = await api.get("/ai/history?limit=120");
            const chrono = [...data].reverse(); // chrono in centre
            setHistory(chrono);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadHistory();
    }, []);

    useEffect(() => {
        if (activeQuestionId) return;
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [history, busy, activeQuestionId]);

    // Group dataMode: derive from most recent answer (or default to mock)
    const dataModeFromAnswers = useMemo(() => {
        for (let i = history.length - 1; i >= 0; i--) {
            const dm = history[i]?.answer?.data_mode;
            if (dm) return dm;
        }
        return "mock";
    }, [history]);

    // History list: dedup + bucket. Show only RECENT_LIMIT by default unless showAllHistory.
    const groups = useMemo(() => {
        const newestFirst = [...history].reverse();
        const deduped = dedupeHistory(newestFirst);
        const filtered = search
            ? deduped.filter((t) =>
                  (t.question?.question || "")
                      .toLowerCase()
                      .includes(search.toLowerCase()),
              )
            : deduped;
        const trimmed = showAllHistory || search ? filtered : filtered.slice(0, RECENT_LIMIT);
        return { buckets: bucketByDate(trimmed), totalUnique: deduped.length, shownCount: trimmed.length };
    }, [history, search, showAllHistory]);

    const visibleTurns = useMemo(() => {
        if (!activeQuestionId) return history;
        return history.filter((t) => t.question?.id === activeQuestionId);
    }, [history, activeQuestionId]);

    const ask = async (q) => {
        const text = (q ?? question).trim();
        if (!text || busy) return;
        setBusy(true);
        try {
            const body = {
                question: text,
                scope: isGroup ? "group" : "entity",
                entity_id: isGroup ? null : selectedEntityId,
            };
            const { data } = await api.post("/ai/ask", body);
            setHistory((h) => [...h, data]);
            setQuestion("");
            setActiveQuestionId(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI request failed");
        } finally {
            setBusy(false);
        }
    };

    const onScroll = (e) => {
        const el = e.currentTarget;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        setShowScrollDown(!atBottom);
    };

    const isMock = dataModeFromAnswers && dataModeFromAnswers !== "production_live";

    return (
        <div
            className="flex h-[calc(100vh-7rem)] -mx-8 -mt-2 bg-white border border-stone-100 rounded-lg overflow-hidden"
            data-testid="ai-expert-page"
        >
            {/* LEFT — History */}
            <aside
                className="w-[260px] flex-shrink-0 border-r border-stone-100 bg-stone-50/60 flex flex-col"
                data-testid="ai-history-pane"
            >
                <div className="px-4 pt-5 pb-3">
                    <button
                        type="button"
                        onClick={() => {
                            setActiveQuestionId(null);
                            setQuestion("");
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-white border border-stone-200 px-3 py-2 text-[13px] text-stone-800 hover:border-stone-300 transition"
                        data-testid="ai-new-conversation"
                    >
                        <Plus size={13} strokeWidth={2} />
                        New question
                    </button>
                </div>
                <div className="px-4 pb-3">
                    <div className="relative">
                        <Search
                            size={12}
                            strokeWidth={2}
                            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400"
                        />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search"
                            className="w-full rounded-md border border-stone-200 bg-white pl-7 pr-3 py-1.5 text-[12.5px] placeholder:text-stone-400 focus:outline-none focus:border-stone-300"
                            data-testid="ai-history-search"
                        />
                    </div>
                </div>
                <div
                    className="flex-1 overflow-y-auto thin-scroll px-2 pb-3"
                    data-testid="ai-history-list"
                >
                    {loading && <Loading testId="ai-history-loading" />}
                    {!loading && groups.buckets.length === 0 && (
                        <div className="text-[13px] text-stone-500 px-3 py-3">
                            {search ? "No matches." : "No conversations yet."}
                        </div>
                    )}
                    {!loading &&
                        groups.buckets.map((g) => (
                            <div key={g.key} className="mb-3">
                                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-medium px-3 mb-1">
                                    {g.label}
                                </div>
                                <ul>
                                    {g.items.map((t) => {
                                        const id = t.question?.id;
                                        const active = id === activeQuestionId;
                                        return (
                                            <li key={id}>
                                                <button
                                                    type="button"
                                                    onClick={() => setActiveQuestionId(id)}
                                                    className={`w-full text-left rounded-md px-3 py-1.5 text-[12.5px] leading-snug transition ${
                                                        active
                                                            ? "bg-white border border-stone-200 text-stone-900 shadow-[0_1px_2px_rgba(24,24,27,0.04)]"
                                                            : "text-stone-700 hover:bg-white hover:text-stone-900"
                                                    }`}
                                                    data-testid={`ai-history-item-${id}`}
                                                >
                                                    <div className="flex items-start gap-2">
                                                        <MessageSquare
                                                            size={11}
                                                            strokeWidth={2}
                                                            className="text-stone-400 mt-0.5 flex-shrink-0"
                                                        />
                                                        <span className="truncate">
                                                            {autoTitle(t.question?.question)}
                                                        </span>
                                                    </div>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    {!loading &&
                        !search &&
                        !showAllHistory &&
                        groups.totalUnique > groups.shownCount && (
                            <button
                                type="button"
                                onClick={() => setShowAllHistory(true)}
                                className="w-full text-left px-3 py-2 text-[12px] text-stone-500 hover:text-stone-800 inline-flex items-center gap-1.5"
                                data-testid="ai-show-older"
                            >
                                <ChevronDown size={11} strokeWidth={2} />
                                Show older · {groups.totalUnique - groups.shownCount} more
                            </button>
                        )}
                </div>
            </aside>

            {/* CENTER — Conversation */}
            <main className="flex-1 flex flex-col min-w-0 relative bg-stone-50/40">
                {isMock && (
                    <div className="px-8 pt-4">
                        <DemoDataBanner />
                    </div>
                )}
                <div
                    ref={scrollRef}
                    onScroll={onScroll}
                    className="flex-1 overflow-y-auto thin-scroll px-8 pt-3"
                    data-testid="ai-conversation"
                >
                    <div className="max-w-[760px] mx-auto pb-10">
                        {loading ? (
                            <div className="py-12">
                                <Loading label="Loading conversation history…" />
                            </div>
                        ) : visibleTurns.length === 0 ? (
                            <NewConversationView onAsk={ask} disabled={busy} />
                        ) : (
                            <>
                                <div className="divide-y divide-stone-100">
                                    {visibleTurns.map((t) => (
                                        <ConversationItem
                                            key={t.question?.id}
                                            turn={t}
                                            onOpenEvidence={(answer) =>
                                                setEvidence({
                                                    ...answer,
                                                    _question: t.question,
                                                })
                                            }
                                        />
                                    ))}
                                </div>
                                {busy && (
                                    <Loading
                                        variant="thinking"
                                        cues={THINKING_CUES}
                                        testId="ai-thinking"
                                    />
                                )}
                                <div ref={endRef} className="h-2" />
                            </>
                        )}
                    </div>
                </div>

                {showScrollDown && (
                    <button
                        type="button"
                        onClick={() =>
                            endRef.current?.scrollIntoView({ behavior: "smooth" })
                        }
                        className="absolute right-8 bottom-40 rounded-full bg-white border border-stone-200 shadow-sm p-2 hover:bg-stone-50"
                        data-testid="ai-scroll-bottom"
                        aria-label="Scroll to bottom"
                    >
                        <ArrowDown size={14} strokeWidth={2} />
                    </button>
                )}

                {/* Composer */}
                <div className="border-t border-stone-100 bg-white px-8 pt-3 pb-5">
                    <div className="max-w-[760px] mx-auto">
                        {!question && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {QUICK_PROMPTS.map((p, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => setQuestion(p.q)}
                                        className="text-[12px] text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-full px-2.5 py-1 transition"
                                        data-testid={`quick-prompt-${i}`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="relative rounded-xl border border-stone-200 bg-white focus-within:border-stone-300 transition">
                            <Textarea
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                rows={2}
                                placeholder={
                                    isGroup
                                        ? "Ask about the group — cash, profit, risk, who needs attention…"
                                        : `Ask about ${selectedEntity?.display_name || "this entity"}…`
                                }
                                className="rounded-xl border-0 bg-transparent focus-visible:ring-0 focus-visible:border-0 resize-none pr-14 text-[14.5px] leading-relaxed min-h-[58px]"
                                data-testid="ai-input"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey))
                                        ask();
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => ask()}
                                disabled={busy || !question.trim()}
                                aria-label="Send"
                                className="absolute right-2.5 bottom-2.5 inline-flex items-center justify-center h-9 w-9 rounded-md text-white transition disabled:opacity-30 disabled:cursor-not-allowed"
                                style={{ background: "var(--brand)" }}
                                data-testid="ai-send"
                            >
                                <Send size={14} strokeWidth={2} />
                            </button>
                        </div>
                        <div className="flex items-center justify-between mt-2 text-[11px] text-stone-400">
                            <span>
                                Scope ·{" "}
                                <span className="text-stone-700 font-medium">
                                    {isGroup
                                        ? "All Crane Sales (group)"
                                        : selectedEntity?.display_name || "Entity"}
                                </span>
                            </span>
                            <span className="hidden md:inline">⌘/Ctrl + Enter to send</span>
                        </div>
                    </div>
                </div>
            </main>

            {/* RIGHT — Evidence drawer */}
            <EvidenceDrawer
                open={!!evidence}
                onClose={() => setEvidence(null)}
                confidence={evidence?.confidence}
                dataBasis={evidence?.data_basis || []}
                evidenceBlocks={evidence?.evidence_blocks || []}
                evidenceRefs={evidence?.evidence_refs || []}
                generatedAt={evidence?.created_at}
                model={evidence?.model}
                dataMode={evidence?.data_mode}
            >
                {evidence?._question?.question && (
                    <div className="text-[13px] text-stone-600 leading-relaxed">
                        <div className="label-soft text-stone-500 mb-1">Question</div>
                        <SafeMarkdown>{evidence._question.question}</SafeMarkdown>
                    </div>
                )}
            </EvidenceDrawer>
        </div>
    );
}
